import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Wallet, Calendar, CreditCard, FileText, CheckCircle2, AlertCircle, UploadCloud, ArrowRight } from 'lucide-react';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { formatINR } from '@/shared/utils/currency';
import { localToday, cn } from '@/shared/utils/utils';
import { api } from '@/shared/api/axios';

export interface RecordAdvanceModalProps {
  open: boolean;
  onClose: () => void;
  orderId: string;
  orderNumber?: string;
  documentType?: string;
  clientName?: string;
  grandTotal?: number;
  currentAmountPaid?: number;
  onSuccess?: () => void;
}

const PAYMENT_METHODS = [
  { value: 'bank_transfer', label: 'Bank Transfer (NEFT / RTGS / IMPS)' },
  { value: 'upi', label: 'UPI (GPay / PhonePe / Paytm / QR)' },
  { value: 'cheque', label: 'Cheque / Demand Draft' },
  { value: 'cash', label: 'Cash Payment' },
  { value: 'other', label: 'Other / Online Gateway' },
];

export function RecordAdvanceModal({
  open,
  onClose,
  orderId,
  orderNumber = 'Document',
  documentType = 'quotation',
  clientName,
  grandTotal = 0,
  currentAmountPaid = 0,
  onSuccess,
}: RecordAdvanceModalProps) {
  const qc = useQueryClient();
  const balanceRemaining = Math.max(0, (grandTotal || 0) - (currentAmountPaid || 0));

  const [amount, setAmount] = useState<string>('');
  const [date, setDate] = useState<string>(localToday());
  const [method, setMethod] = useState<string>('bank_transfer');
  const [reference, setReference] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [proofFile, setProofFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      // Suggest remaining balance or empty if fully paid
      setAmount(balanceRemaining > 0 ? String(balanceRemaining) : '');
      setDate(localToday());
      setMethod('bank_transfer');
      setReference('');
      setNotes('');
      setProofFile(null);
    }
  }, [open, balanceRemaining]);

  const numAmount = Number(amount) || 0;
  const newTotalAdvance = currentAmountPaid + numAmount;
  const newBalance = Math.max(0, grandTotal - newTotalAdvance);
  const isOverpayment = grandTotal > 0 && newTotalAdvance > grandTotal;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!amount || numAmount <= 0) {
      toast.error('Please enter a valid advance payment amount (> 0).');
      return;
    }
    if (!date) {
      toast.error('Payment date is required.');
      return;
    }

    setIsSubmitting(true);
    try {
      if (proofFile) {
        const formData = new FormData();
        formData.append('amount', String(numAmount));
        formData.append('method', method);
        formData.append('date', date);
        formData.append('reference', reference.trim());
        formData.append('notes', notes.trim());
        formData.append('proof', proofFile);

        await api.post(`/api/sales-ops/orders/${orderId}/payment`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        await api.post(`/api/sales-ops/orders/${orderId}/payment`, {
          amount: numAmount,
          method,
          date,
          reference: reference.trim(),
          notes: notes.trim(),
        });
      }

      toast.success(`Advance payment of ${formatINR(numAmount)} recorded successfully!`);

      // Invalidate relevant caches
      await Promise.allSettled([
        qc.invalidateQueries({ queryKey: ['sales-doc'] }),
        qc.invalidateQueries({ queryKey: ['sales-orders'] }),
        qc.invalidateQueries({ queryKey: ['quotations'] }),
        qc.invalidateQueries({ queryKey: ['invoices'] }),
        qc.invalidateQueries({ queryKey: ['invoices-direct'] }),
        qc.invalidateQueries({ queryKey: ['invoice-requests'] }),
        qc.invalidateQueries({ queryKey: ['accounts', 'sales-invoices'] }),
        qc.invalidateQueries({ queryKey: ['deals'] }),
      ]);

      if (onSuccess) onSuccess();
      onClose();
    } catch (err: any) {
      console.error('[RecordAdvanceModal] submit failed:', err);
      const msg = err?.response?.data?.error || err?.message || 'Failed to record advance payment.';
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-success/10 text-success-text border border-success/20">
            <Wallet className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-base font-bold text-on-surface">Record Advance Amount Received</h2>
            <p className="text-xs text-on-surface-variant font-medium">
              Update advance payment for {orderNumber} {clientName ? `• ${clientName}` : ''}
            </p>
          </div>
        </div>
      }
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4 pt-1">
        {/* Financial Summary Card */}
        <div className="rounded-xl border border-border bg-surface-container-low/60 p-3.5 space-y-2">
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="rounded-lg bg-surface p-2 border border-border/60 shadow-xs">
              <span className="text-[10px] uppercase font-bold tracking-wider text-on-surface-subtle">Grand Total</span>
              <p className="text-sm font-bold tabular-nums text-on-surface mt-0.5">{formatINR(grandTotal)}</p>
            </div>
            <div className="rounded-lg bg-success/5 p-2 border border-success/20 shadow-xs">
              <span className="text-[10px] uppercase font-bold tracking-wider text-success-text">Advance Received</span>
              <p className="text-sm font-bold tabular-nums text-success-text mt-0.5">{formatINR(currentAmountPaid)}</p>
            </div>
            <div className="rounded-lg bg-surface p-2 border border-border/60 shadow-xs">
              <span className="text-[10px] uppercase font-bold tracking-wider text-on-surface-subtle">Balance Due</span>
              <p className={cn("text-sm font-bold tabular-nums mt-0.5", balanceRemaining > 0 ? "text-danger-text" : "text-success-text")}>
                {formatINR(balanceRemaining)}
              </p>
            </div>
          </div>

          {numAmount > 0 && (
            <div className="flex items-center justify-between text-xs pt-1 px-1 border-t border-border/50 text-on-surface-variant">
              <span>After recording this payment:</span>
              <span className="font-semibold text-primary flex items-center gap-1">
                New Advance: {formatINR(newTotalAdvance)}
                <ArrowRight className="h-3 w-3" />
                Remaining: {formatINR(newBalance)}
              </span>
            </div>
          )}

          {isOverpayment && (
            <div className="flex items-start gap-1.5 rounded-lg bg-info/10 border border-info/20 p-2 text-xs text-info-text">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>
                <strong>Advance exceeds document total:</strong> The excess amount of {formatINR(newTotalAdvance - grandTotal)} will be safely tracked as client credit balance for future orders / adjustments.
              </span>
            </div>
          )}
        </div>

        {/* Payment Amount & Date */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold text-on-surface block mb-1">
              Advance Amount Received (₹) <span className="text-danger-text">*</span>
            </label>
            <Input
              type="number"
              step="any"
              min="0.01"
              required
              autoFocus
              placeholder="e.g. 25000"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-on-surface block mb-1 flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5 text-on-surface-variant" /> Payment Date <span className="text-danger-text">*</span>
            </label>
            <Input
              type="date"
              required
              value={date}
              onChange={(e) => setDate(e.target.value)}
              disabled={isSubmitting}
            />
          </div>
        </div>

        {/* Payment Method & Reference */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold text-on-surface block mb-1 flex items-center gap-1">
              <CreditCard className="h-3.5 w-3.5 text-on-surface-variant" /> Payment Mode <span className="text-danger-text">*</span>
            </label>
            <Select
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              options={PAYMENT_METHODS}
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-on-surface block mb-1">
              Ref / UTR / Cheque / Txn ID
            </label>
            <Input
              type="text"
              placeholder="e.g. UTR1234567890"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              disabled={isSubmitting}
            />
          </div>
        </div>

        {/* Remarks / Notes */}
        <div>
          <label className="text-xs font-semibold text-on-surface block mb-1">
            Notes / Payment Remarks
          </label>
          <Input
            type="text"
            placeholder="e.g. 50% advance for dispatch clearance, client advance to deduct in next order"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={isSubmitting}
          />
        </div>

        {/* Optional Proof Screenshot / File */}
        <div>
          <label className="text-xs font-semibold text-on-surface block mb-1 flex items-center gap-1">
            <UploadCloud className="h-3.5 w-3.5 text-on-surface-variant" /> Payment Receipt / Proof (Optional)
          </label>
          <input
            type="file"
            accept="image/*,.pdf"
            onChange={(e) => setProofFile(e.target.files?.[0] || null)}
            disabled={isSubmitting}
            className="block w-full text-xs text-on-surface-variant file:mr-2.5 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-primary/10 file:text-primary hover:file:bg-primary/20 file:cursor-pointer cursor-pointer border border-border rounded-lg bg-surface p-1"
          />
          {proofFile && (
            <p className="mt-1 text-[11px] text-success-text font-medium flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3" /> Selected: {proofFile.name} ({(proofFile.size / 1024).toFixed(1)} KB)
            </p>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-2 pt-3 border-t border-border">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
            className="text-xs"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            loading={isSubmitting}
            disabled={isSubmitting || !amount || numAmount <= 0}
            className="text-xs font-bold gap-1.5 bg-success text-white hover:bg-success/90"
          >
            <CheckCircle2 className="h-4 w-4" />
            {numAmount > 0 ? `Record Advance of ${formatINR(numAmount)}` : 'Record Advance'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
